/**
 * Brain graph mock (T13). Shapes the 10-agent roster + shared tools into a
 * node/edge graph for react-flow. Agent 0 = orchestrator hub; every other
 * agent radiates from it; tools hang off the agents that use them.
 *
 * Labels/roles follow shared/roster.json (go-live-readiness.md §4). Nodes are
 * id-keyed (agent-<displayId>), matching the store's agent ids, so the graph
 * status-sync in BrainPage and the sidebar lookup in NodeDetailSidebar stay
 * correct after this remap — the old Sage/Aegis/Compass/Prism slots 7–10 are
 * now RELAY/PRISM/WARDEN per the semantic (not ordinal) canonical mapping:
 * 7 = RELAY (Support, agent_8_support), 8 = FORGE (Engineering,
 * agent_6_codebase), 9 = PRISM (Data Pipeline, agent_9_billing),
 * 10 = WARDEN (QA & Testing, agent_7_qa).
 */

const ROLE_SUBSTRATE = {
  1: { label: 'Atlas', role: 'Orchestrator', kind: 'root' },
  2: { label: 'Scout', role: 'Research', kind: 'sub' },
  3: { label: 'Quill', role: 'Content', kind: 'sub' },
  4: { label: 'Vector', role: 'Growth', kind: 'sub' },
  5: { label: 'Ledger', role: 'Finance', kind: 'sub' },
  6: { label: 'Sentinel', role: 'Compliance', kind: 'sub' },
  7: { label: 'Relay', role: 'Support', kind: 'sub' },
  8: { label: 'Forge', role: 'Engineering', kind: 'sub' },
  9: { label: 'Prism', role: 'Data Pipeline', kind: 'sub' },
  10: { label: 'Warden', role: 'QA & Testing', kind: 'sub' }
};

export const BRAIN_STATS = {
  decisions: 1204,
  autonomousRate: 0.87,
  routedToday: 318,
  escalations: 23
};

const TOOL_ATTACHMENTS = {
  vector_store: [7, 2],
  github: [6],
  stripe: [5],
  telegram: [1, 8],
  llm_router: [10, 1]
};

export function buildBrainGraph() {
  const nodes = [
    {
      id: 'agent-0',
      type: 'orchestrator',
      data: {
        name: 'Atlas — Core Orchestrator',
        role: 'Agent 0 · routes all work',
        stats: { decisions: BRAIN_STATS.decisions, autonomy: BRAIN_STATS.autonomousRate }
      },
      position: { x: 0, y: 0 }
    }
  ];

  const edges = [];

  Object.entries(ROLE_SUBSTRATE).forEach(([id, meta]) => {
    if (meta.kind === 'root') return;
    const agentId = Number(id);

    nodes.push({
      id: `agent-${agentId}`,
      type: 'subAgent',
      data: { name: meta.label, role: meta.role, agentId },
      position: { x: 0, y: 0 } // computed by layout below
    });

    edges.push({
      id: `edge-root-${agentId}`,
      source: 'agent-0',
      target: `agent-${agentId}`,
      animated: false,
      data: { kind: 'orchestration' }
    });
  });

  Object.entries(TOOL_ATTACHMENTS).forEach(([tool, agentIds]) => {
    nodes.push({
      id: `tool-${tool}`,
      type: 'tool',
      data: { name: tool.replace(/_/g, ' '), tool },
      position: { x: 0, y: 0 }
    });

    agentIds.forEach((agentId) => {
      edges.push({
        id: `edge-${tool}-${agentId}`,
        source: `agent-${agentId}`,
        target: `tool-${tool}`,
        animated: false,
        data: { kind: 'tool' }
      });
    });
  });

  return { nodes, edges };
}
